import React, { useEffect, useState } from 'react';
import {
    Box,
    VStack,
    Text,
    Spinner,
    Alert,
    AlertIcon,
    HStack,
    Badge,
    useBreakpointValue
} from '@chakra-ui/react';
import { useHotspots } from '../hooks/useHotspots';
import { getSeverityConfig } from '../utils/hotspots';

interface HotspotsStatsProps {
    isSatellite?: boolean;
    hotspots?: any[];
    loading?: boolean;
    error?: string | null;
}

const HotspotsStats: React.FC<HotspotsStatsProps> = ({ 
    isSatellite = false,
    hotspots: propsHotspots,
    loading: propsLoading,
    error: propsError
}) => {
    const {
        hotspots: hookHotspots,
        loading: hookLoading,
        error: hookError,
        fetchHotspots
    } = useHotspots();

    // Use props if provided, otherwise use hook data
    const hotspots = propsHotspots !== undefined ? propsHotspots : hookHotspots;
    const loading = propsLoading !== undefined ? propsLoading : hookLoading;
    const error = propsError !== undefined ? propsError : hookError;

    const [severityStats, setSeverityStats] = useState<Record<number, number>>({});

    // Use breakpoint values at the top level
    const spacingMain = useBreakpointValue({ base: 2, md: 3 });
    const spacingSeverity = useBreakpointValue({ base: 1, md: 2 });
    const paddingTop = useBreakpointValue({ base: 2, md: 2 });

    // Dynamic text colors based on satellite mode
    const textColors = {
        primary: isSatellite ? "white" : "gray.800",
        secondary: isSatellite ? "gray.200" : "gray.700", 
        tertiary: isSatellite ? "gray.300" : "gray.600"
    };

    useEffect(() => {
        // Only fetch if not controlled by props
        if (propsHotspots === undefined) {
            fetchHotspots();
        }
    }, [fetchHotspots, propsHotspots]);

    useEffect(() => {
        // Hitung statistik severity
        const stats: Record<number, number> = {};
        hotspots.forEach(hotspot => {
            stats[hotspot.severity] = (stats[hotspot.severity] || 0) + 1;
        });
        setSeverityStats(stats);
    }, [hotspots]);

    if (loading) {
        return (
            <Box 
                p={2} 
                textAlign="center" 
            >
                <Spinner size="lg" color="blue.500" />
                <Text mt={2} color={textColors.secondary} fontSize="sm">Loading hotspots data...</Text>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2}>
                <Alert status="error" borderRadius="md" size="sm">
                    <AlertIcon />
                    <Text fontSize="sm">
                        Error loading hotspots: {error}
                    </Text>
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            <VStack spacing={spacingMain} align="stretch">
                {/* Total Count */}
                <HStack justify="space-between" align="center">
                    <Text fontSize="sm" fontWeight="semibold" color={textColors.secondary}>
                        Total Titik Panas:
                    </Text>
                    <Badge variant="solid" colorScheme="blue" fontSize="sm" px={2} py={1}>
                        {hotspots.length}
                    </Badge>
                </HStack>

                {/* Severity Breakdown */}
                {Object.keys(severityStats).length > 0 && (
                    <Box>
                        <Text fontSize="xs" fontWeight="semibold" color={textColors.tertiary} mb={2}>
                            Berdasarkan Tingkat Keparahan:
                        </Text>
                        
                        <VStack spacing={spacingSeverity} align="stretch">
                            {Object.entries(severityStats).map(([severity, count]) => {
                                const severityNum = Number(severity);
                                const config = getSeverityConfig(severityNum);

                                if (!config) return null;

                                return (
                                    <HStack key={severity} justify="space-between" align="center">
                                        <HStack spacing={2} flex={1}>
                                            <Box
                                                w="8px"
                                                h="8px"
                                                bg={config.color}
                                                borderRadius="full"
                                                border="1px solid"
                                                borderColor="gray.300"
                                                flexShrink={0}
                                            />
                                            <Text fontSize="xs" color={textColors.primary} noOfLines={1}>
                                                {config.label}
                                            </Text>
                                        </HStack>
                                        <Badge 
                                            colorScheme="blue" 
                                            variant="subtle" 
                                            fontSize="xs"
                                            minW="20px"
                                            textAlign="center"
                                        >
                                            {count}
                                        </Badge>
                                    </HStack>
                                );
                            })}
                        </VStack>
                    </Box>
                )}

                {/* Latest Hotspot Info */}
                {hotspots.length > 0 && (
                    <Box 
                        pt={paddingTop} 
                        borderTop="1px solid" 
                        borderColor="gray.200"
                    >
                        <Text fontSize="xs" color={textColors.tertiary}>
                            <Text as="span" fontWeight="semibold">Deteksi Terakhir:</Text>
                            <br />
                            {new Date(hotspots[0].acq_datetime_wib).toLocaleString('id-ID', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </Text>
                    </Box>
                )}
            </VStack>
        </Box>
    );
};

export default HotspotsStats;